import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { IndianRupee, Pencil, Trash2 } from 'lucide-react'
import { Package } from '../../../../Data/package.dto'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import { setCurrentPackage } from '../../../../Redux/Slice/Dashboard/PackageSlice'
import { useNavigate } from 'react-router-dom'
import DeletePopup from '../Folder/DeletePopup'
import {
  deletePackageAPI,
  updatePackagePriceStatusAPI,
} from '../../../../Redux/ApiCalls/Dashboard/PackageAPI'
import { device } from '../../Utlis/media'
import { usePermissions } from '../../../../hooks/usePermissions'

interface PackageItemProps {
  packageData: Package
}

const gradient = 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'

const PackageItem: React.FC<PackageItemProps> = ({ packageData }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [isDeletePopup, setIsDeletePopup] = useState(false)
  const { user } = useAppSelector((state) => state.auth)
  const { hasPermission } = usePermissions()
  const canUpdate = hasPermission('packages.update')
  const canDelete = hasPermission('packages.delete')

  const handleClick = () => {
    dispatch(setCurrentPackage(packageData))
    navigate('/dashboard/package/single')
  }

  const handleDeletePackage = (id: number) => {
    dispatch(deletePackageAPI({ package_id: id }))
    setIsDeletePopup(false)
  }

  const handleToggleDynamic = () => {
    const newStatus = !packageData.package_price_request
    dispatch(
      updatePackagePriceStatusAPI({
        data: {
          package_id: packageData.id,
          package_price_request: newStatus,
        },
        user_id: user?.id,
      })
    )
  }

  const formatToINR = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const showPrice = packageData.package_price_request

  return (
    <>
      {createPortal(
        isDeletePopup ? (
          <DeletePopup
            text="Are you sure you want to delete package?"
            buttonText="Delete"
            cancel={() => setIsDeletePopup(false)}
            Delete={() => handleDeletePackage(packageData.id)}
          />
        ) : null,
        document.body
      )}

      <CardContainer>
        {/* <CardAccent /> */}

        <MainRow>
          <InfoBlock>
            <IconWrap>
              <IndianRupee size={18} />
            </IconWrap>
            <InfoText>
              <PackageName>{packageData.title}</PackageName>
              <PriceRow>
                <PricePill>{formatToINR(packageData.price)}</PricePill>
                {!showPrice && <HiddenPriceNote>Hidden from clients</HiddenPriceNote>}
              </PriceRow>
            </InfoText>
          </InfoBlock>

          {canUpdate && (
            <ToggleBlock>
              <ToggleLabel>{showPrice ? 'Price visible' : 'Price hidden'}</ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={showPrice}
                onClick={handleToggleDynamic}
                aria-label={showPrice ? 'Hide price from clients' : 'Show price to clients'}
              >
                <ToggleCircle $active={showPrice} />
              </ToggleSwitch>
            </ToggleBlock>
          )}

          {(canUpdate || canDelete) && (
            <ActionBlock>
              {canUpdate && (
                <EditBtn type="button" onClick={handleClick} aria-label="Edit package">
                  <Pencil size={16} />
                </EditBtn>
              )}
              {canDelete && (
                <DeleteBtn type="button" onClick={() => setIsDeletePopup(true)} aria-label="Delete package">
                  <Trash2 size={16} />
                </DeleteBtn>
              )}
            </ActionBlock>
          )}
        </MainRow>
      </CardContainer>
    </>
  )
}

export default PackageItem

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 18px 20px 18px 24px;
  background: #fff;
  border-radius: 18px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(201, 77, 170, 0.1);
  }
`

const CardAccent = styled.div`
  position: absolute;
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 4px;
  border-radius: 0 999px 999px 0;
  background: ${gradient};
`

const MainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media ${device.tablet} {
    flex-wrap: wrap;
    gap: 14px;
  }
`

const InfoBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
`

const IconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.1);
  flex-shrink: 0;
`

const InfoText = styled.div`
  min-width: 0;
`

const PackageName = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #171717;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  flex-wrap: wrap;
`

const PricePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.1);
  border: 1px solid rgba(201, 77, 170, 0.14);
`

const HiddenPriceNote = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.45);
`

const ToggleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 12px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  flex-shrink: 0;

  @media ${device.tablet} {
    width: 100%;
    justify-content: space-between;
  }
`

const ToggleLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.62);
  white-space: nowrap;
`

const ToggleSwitch = styled.button<{ $active: boolean }>`
  width: 46px;
  height: 26px;
  border-radius: 999px;
  border: none;
  padding: 0;
  background: ${({ $active }) => ($active ? gradient : 'rgba(23, 23, 23, 0.18)')};
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
`

const ToggleCircle = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ $active }) => ($active ? '23px' : '3px')};
  width: 20px;
  height: 20px;
  background-color: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  transition: left 0.2s ease;
`

const ActionBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const EditBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background: ${gradient};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease;
  &:hover { transform: translateY(-1px); }
`

const DeleteBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(228, 72, 98, 0.25);
  background: rgba(228, 72, 98, 0.06);
  color: #e44862;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { background: rgba(228, 72, 98, 0.12); }
`
